import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { advance, DishRef, groupByDish, isActive, Lane, laneOf, LATE_MINUTES, minutesSince, NEXT_STEP } from '../lib/kitchen';
import { usePollWhileOffline } from '../lib/live';
import { usePref } from '../lib/prefs';
import { main } from '../lib/text';
import type { LineStatus, Order, OrderLine, Settings } from '../lib/types';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

type View = 'table' | 'dish';
type Kind = 'all' | 'dine-in' | 'takeout';

const LANES: { id: Lane; title: string; tint: string; btn: string }[] = [
  { id: 'new', title: 'ใหม่ · New', tint: '#BBD3FF', btn: 'เริ่มทั้งหมด · Start all' },
  { id: 'cooking', title: 'กำลังทำ · Cooking', tint: '#FFE2A6', btn: 'เสร็จทั้งหมด · All ready' },
  { id: 'ready', title: 'รอเสิร์ฟ · To serve', tint: '#BFEBD0', btn: 'เสิร์ฟทั้งหมด · All served' },
];
const STEP_LABEL: Partial<Record<LineStatus, string>> = { queued: 'เริ่ม', cooking: 'เสร็จ', ready: 'เสิร์ฟ' };

/**
 * /kitchen: the dish is the unit of work. "By table" shows tickets with a tap per dish; "By dish" groups the same
 * dish across tables so the kitchen can cook them together. Filters: station (per device) and dine-in/takeout.
 */
export function Kitchen({ path }: { path: string }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = usePref<View>('kitchen.view', 'table');
  const [station, setStation] = usePref<string>('kitchen.station', 'all');
  const [kind, setKind] = usePref<Kind>('kitchen.kind', 'all');

  const { s, online } = useStaff((e) => {
    if (e.kind !== 'order') return;
    const o = e.order as Order;
    setOrders((prev) => {
      const rest = prev.filter((x) => x.id !== o.id);
      return isActive(o) ? [...rest, o].sort((a, b) => a.placedAt.localeCompare(b.placedAt) || a.number - b.number) : rest;
    });
  }, () => void load());

  const load = useCallback(async () => {
    if (!s) return;
    try {
      const [k, r] = await Promise.all([
        api<{ orders: Order[] }>('GET', '/kitchen', { token: s.token }),
        api<{ settings: Settings }>('GET', '/restaurant', { token: s.token }),
      ]);
      setOrders(k.orders);
      setSettings(r.settings);
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, [s]);
  useEffect(() => { void load(); }, [load]);
  usePollWhileOffline(online, () => void load());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  const visible = useCallback((l: OrderLine) => station === 'all' || (l.station ?? 'kitchen') === station, [station]);
  const shown = useMemo(() => orders.filter((o) => (kind === 'all' || (o.kind ?? 'dine-in') === kind) && o.lines.some((l) => visible(l) && ['queued', 'cooking', 'ready'].includes(l.status))), [orders, kind, visible]);

  const move = async (status: LineStatus, refs: { sessionId: string; orderId: string; lineId: string }[]) => {
    if (!s || !refs.length) return;
    setBusy(true);
    setErr(null);
    try {
      for (let i = 0; i < refs.length; i += 50) {
        await api('PUT', '/kitchen/lines', { token: s.token, body: { status, lines: refs.slice(i, i + 50) } });
      }
    } catch (e) { handleAuthError(e); setErr((e as Error).message); void load(); } finally { setBusy(false); }
  };
  const ref = (o: Order, l: OrderLine) => ({ sessionId: o.sessionId, orderId: o.id, lineId: l.id });
  const toServe = shown.flatMap((o) => o.lines.filter((l) => visible(l) && l.status === 'ready').map((l) => ({ o, l })));

  return (
    <Shell path={path} online={online} sub={`${shown.length} tickets`}>
      <div className="panel" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <nav className="tabs" aria-label="Kitchen view">
          <button className="tab" aria-current={view === 'table' ? 'page' : undefined} onClick={() => setView('table')}>ตามโต๊ะ · By table</button>
          <button className="tab" aria-current={view === 'dish' ? 'page' : undefined} onClick={() => setView('dish')}>ตามเมนู · By dish</button>
        </nav>
        <nav className="tabs" aria-label="Order type">
          {([['all', 'ทั้งหมด · All'], ['dine-in', 'ทานที่ร้าน · Dine-in'], ['takeout', 'กลับบ้าน · Takeout']] as const).map(([k, label]) => (
            <button key={k} className="tab" aria-current={kind === k ? 'page' : undefined} onClick={() => setKind(k)}>{label}</button>
          ))}
        </nav>
        {settings && settings.stations.length > 1 && (
          <nav className="tabs" aria-label="Station">
            <button className="tab" aria-current={station === 'all' ? 'page' : undefined} onClick={() => setStation('all')}>ทุกจุด · All stations</button>
            {settings.stations.map((st) => (
              <button key={st.id} className="tab" aria-current={station === st.id ? 'page' : undefined} onClick={() => setStation(st.id)}>{main(st.name)}</button>
            ))}
          </nav>
        )}
      </div>
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)', marginBottom: 12 }}>{err}</div>}

      {view === 'table' ? (
        <div className="lanes">
          {LANES.map((lane) => {
            const tickets = shown.filter((o) => laneOf(o, visible) === lane.id);
            return (
              <section key={lane.id} className="panel" aria-label={lane.title}>
                <div className="lane-head">
                  <h2 className="display" style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>{lane.title}</h2>
                  <span className="count" style={{ background: lane.tint }}>{tickets.length}</span>
                </div>
                {tickets.length === 0 && <div className="empty-lane">—</div>}
                {tickets.map((o) => {
                  const mins = minutesSince(o.placedAt, now);
                  const isLate = lane.id !== 'ready' && mins >= LATE_MINUTES;
                  const step = advance(o, visible);
                  return (
                    <article key={o.id} className={`kt${isLate ? ' late' : ''}`}>
                      <div className="row">
                        <span className="display" style={{ fontSize: 20 }}>
                          {o.kind === 'takeout' ? <span className="tchip" style={{ background: '#6A44D8', marginRight: 6 }}>กลับบ้าน</span> : 'โต๊ะ '}{o.tableName}
                          <span className="muted" style={{ fontSize: 14 }}> #{o.number}</span>
                        </span>
                        <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isLate ? '#C42A30' : undefined }}>{mins} min</span>
                      </div>
                      <ul>
                        {o.lines.filter((l) => visible(l) && l.status !== 'cancelled').map((l) => (
                          <li key={l.id} className={l.status === 'served' ? 'done' : ''} style={{ alignItems: 'center' }}>
                            <span className="q">{l.qty}×</span>
                            <span style={{ flex: 1 }}>
                              {main(l.name)}
                              {l.choices.length > 0 && <span className="opts"> · {l.choices.map((c) => main(c.name)).join(', ')}</span>}
                              {l.note && <span className="knote" style={{ display: 'block', marginTop: 2 }}>{l.note}</span>}
                            </span>
                            {NEXT_STEP[l.status] && (
                              <button className={`dish-step ${l.status}`} disabled={busy} onClick={() => void move(NEXT_STEP[l.status]!, [ref(o, l)])}
                                aria-label={`${STEP_LABEL[l.status]} ${main(l.name)}`}>{STEP_LABEL[l.status]}</button>
                            )}
                          </li>
                        ))}
                      </ul>
                      {step && step.lineIds.length > 1 && (
                        <button className={`kbtn ${lane.id}`} disabled={busy} onClick={() => void move(step.status, step.lineIds.map((lineId) => ({ sessionId: o.sessionId, orderId: o.id, lineId })))}>{lane.btn}</button>
                      )}
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="lanes">
          <section className="panel" aria-label="Dishes to cook" style={{ gridColumn: 'span 2' }}>
            <div className="lane-head"><h2 className="display" style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>ต้องทำ · To cook</h2></div>
            {groupByDish(shown, visible).map((g) => {
              const late = minutesSince(g.oldest, now) >= LATE_MINUTES;
              const qty = (refs: DishRef[]) => refs.reduce((n, r) => n + r.qty, 0);
              return (
                <article key={g.key} className={`kt${late ? ' late' : ''}`}>
                  <div className="row">
                    <span className="display" style={{ fontSize: 22 }}>{g.total}× {main(g.name)}</span>
                    <span style={{ fontWeight: 700 }}>{minutesSince(g.oldest, now)} min</span>
                  </div>
                  {(g.choices.length > 0 || g.note) && <span className="opts" style={{ fontSize: 14 }}>{[...g.choices.map(main), g.note].filter(Boolean).join(' · ')}</span>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {[...g.queued, ...g.cooking].map((r) => (
                      <span key={`${r.orderId}${r.lineId}`} className={`status ${r.status}`}>{r.table} ×{r.qty}</span>
                    ))}
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    {g.queued.length > 0 && <button className="kbtn new" style={{ flex: 1 }} disabled={busy} onClick={() => void move('cooking', g.queued)}>เริ่ม {qty(g.queued)} · Start {qty(g.queued)}</button>}
                    {g.cooking.length > 0 && <button className="kbtn cooking" style={{ flex: 1 }} disabled={busy} onClick={() => void move('ready', g.cooking)}>เสร็จ {qty(g.cooking)} · Done {qty(g.cooking)}</button>}
                  </div>
                </article>
              );
            })}
            {groupByDish(shown, visible).length === 0 && <div className="empty-lane">—</div>}
          </section>
          <section className="panel" aria-label="Ready to serve">
            <div className="lane-head">
              <h2 className="display" style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>รอเสิร์ฟ · To serve</h2>
              <span className="count" style={{ background: '#BFEBD0' }}>{toServe.length}</span>
            </div>
            {toServe.length === 0 && <div className="empty-lane">—</div>}
            {toServe.map(({ o, l }) => (
              <div key={o.id + l.id} className="kt row" style={{ padding: 10 }}>
                <span><b>{o.kind === 'takeout' ? '' : 'โต๊ะ '}{o.tableName}</b> · {l.qty}× {main(l.name)}</span>
                <button className="dish-step ready" disabled={busy} onClick={() => void move('served', [ref(o, l)])}>เสิร์ฟ</button>
              </div>
            ))}
          </section>
        </div>
      )}
    </Shell>
  );
}
